import React, { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { filterMembersForSession } from "../utils/masonicDegrees";
import {
  X,
  QrCode,
  RefreshCcw,
  Users,
  Check,
  UserCheck,
  Compass,
  Printer,
  Copy,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SessionQRModal = ({ isOpen, onClose, session, members = [], onCheckedIn }) => {
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualCheckin, setManualCheckin] = useState("");

  const fetchToken = async () => {
    if (!session?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sessions/${session.id}/qr-token`);
      setTokenData(res.data);
    } catch (err) {
      toast.error("Erro ao gerar QR Code da sessão.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && session?.id) {
      fetchToken();
    }
  }, [isOpen, session?.id]);

  if (!isOpen || !session) return null;

  const publicCheckinURL = `${window.location.origin}/checkin?s=${session.id}&t=${tokenData?.token || ""}`;

  const copyLink = () => {
    navigator.clipboard.writeText(publicCheckinURL);
    toast.success("Link de check-in copiado!");
  };

  const doManualCheckin = async () => {
    if (!manualCheckin) {
      toast.error("Selecione um Irmão para registrar presença.");
      return;
    }
    try {
      const res = await axios.post(`${API}/sessions/${session.id}/qr-checkin`, {
        member_id: manualCheckin,
        token: tokenData?.token,
      });
      if (res.data.already_registered) {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      setManualCheckin("");
      if (onCheckedIn) onCheckedIn();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar presença.");
    }
  };

  const printQR = () => {
    window.print();
  };

  const presentIds = new Set(session.present_member_ids || []);
  const totalPresentes = presentIds.size;
  const totalMembros = members.length;

  return (
    <div
      data-testid="session-qr-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 print:bg-white print:p-0"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-[#0B111E] border border-[#D4AF37]/40 rounded-2xl shadow-2xl overflow-hidden print:border-black print:shadow-none print:bg-white print:text-black"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#D4AF37]/20 bg-[#080D18] flex items-center justify-between print:bg-white print:border-black">
          <div>
            <div className="flex items-center space-x-2 text-xs text-[#D4AF37] uppercase tracking-widest font-bold">
              <Compass className="w-3.5 h-3.5" />
              <span>Presença por QR Code</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-white mt-1 print:text-black">
              Sessão nº {session.session_number} — {session.title}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5 print:text-black">
              {session.date} • Grau {session.degree_required}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="qr-modal-close"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 print:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* QR Code */}
          <div className="flex flex-col items-center justify-center bg-white p-6 rounded-xl border border-[#D4AF37]/30">
            {tokenData?.token ? (
              <QRCodeCanvas
                value={publicCheckinURL}
                size={220}
                bgColor="#ffffff"
                fgColor="#0B111E"
                level="H"
                includeMargin={true}
                data-testid="session-qrcode"
              />
            ) : (
              <div className="w-56 h-56 flex items-center justify-center text-slate-400 text-xs">
                {loading ? "Gerando QR Code..." : "QR indisponível"}
              </div>
            )}
            <p className="text-[10px] mt-3 text-slate-600 font-mono uppercase tracking-wider text-center">
              Ir∴ deve escanear com o celular para registrar presença
            </p>
          </div>

          {/* Info & Actions */}
          <div className="space-y-4 print:hidden">
            <div className="bg-[#131C2E] rounded-xl p-4 border border-slate-700/60">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400 uppercase tracking-wider">Presentes</span>
                <span className="text-[#D4AF37] font-bold">
                  {totalPresentes} / {totalMembros}
                </span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-[#D4AF37]"
                  style={{ width: `${totalMembros ? (totalPresentes / totalMembros) * 100 : 0}%` }}
                />
              </div>
            </div>

            <button
              data-testid="qr-btn-copy-link"
              onClick={copyLink}
              className="w-full flex items-center justify-center space-x-2 bg-[#131C2E] hover:bg-[#1E293B] text-[#D4AF37] border border-[#D4AF37]/40 py-2.5 rounded-lg text-xs uppercase tracking-wider font-semibold"
            >
              <Copy className="w-3.5 h-3.5" />
              <span>Copiar link de check-in</span>
            </button>

            <button
              data-testid="qr-btn-refresh-token"
              onClick={fetchToken}
              className="w-full flex items-center justify-center space-x-2 bg-[#131C2E] hover:bg-[#1E293B] text-white border border-slate-700 py-2.5 rounded-lg text-xs uppercase tracking-wider font-semibold"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              <span>Renovar QR</span>
            </button>

            <button
              data-testid="qr-btn-print"
              onClick={printQR}
              className="w-full flex items-center justify-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 py-2.5 rounded-lg text-xs uppercase tracking-wider font-bold"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Imprimir para colocar no Templo</span>
            </button>

            <div className="pt-3 border-t border-slate-800/80">
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1.5">
                <UserCheck className="w-3 h-3" />
                <span>Registrar presença manualmente</span>
              </label>
              <div className="flex space-x-2 mt-1.5">
                <select
                  data-testid="qr-manual-select"
                  value={manualCheckin}
                  onChange={(e) => setManualCheckin(e.target.value)}
                  className="flex-1 bg-[#0B111E] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-[#D4AF37] outline-none"
                >
                  <option value="">Selecione o Ir∴</option>
                  {members
                    .filter((m) => !presentIds.has(m.id))
                    .filter((m) => filterMembersForSession([m], session.degree_required, session.date).length > 0)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} — CIM {m.cim} ({m.degree})
                      </option>
                    ))}
                </select>
                <button
                  data-testid="qr-manual-submit"
                  onClick={doManualCheckin}
                  disabled={!manualCheckin}
                  className="flex items-center space-x-1 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-xs font-bold"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
